import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    FiAlertCircle,
    FiBookOpen,
    FiCheck,
    FiEdit,
    FiEye,
    FiFileText,
    FiFilter,
    FiDownload,
    FiHelpCircle,
    FiLoader,
    FiPlus,
    FiRefreshCw,
    FiSearch,
    FiSliders,
    FiShield,
    FiTrash2,
    FiUploadCloud,
    FiX,
} from 'react-icons/fi';
import {FaQuestionCircle} from 'react-icons/fa';
import {Link, useSearchParams} from 'react-router-dom';
import './QuestionBank.css';
import {accountsApi, getErrorMessage, questionsApi, resourcesApi} from '../services/api';
import {getTextDirection} from '../ExamPage/textDirection';
import MathText from '../components/MathText';
import useAuthRole from '../hooks/useAuthRole';

const statusLabels = {
    pending: 'در انتظار',
    ready: 'آماده',
    failed: 'ناموفق',
};

const statusFilters = [
    {value: 'all', label: 'همه'},
    {value: 'ready', label: 'آماده'},
    {value: 'pending', label: 'در انتظار'},
    {value: 'failed', label: 'ناموفق'},
];

const reviewStatusLabels = {
    draft: 'پیش‌نویس',
    needs_review: 'نیازمند بازبینی',
    approved: 'تاییدشده',
    rejected: 'ردشده',
};

const reviewFilters = [
    {value: 'all', label: 'همه'},
    {value: 'needs_review', label: 'نیازمند بازبینی'},
    {value: 'approved', label: 'تاییدشده'},
    {value: 'draft', label: 'پیش‌نویس'},
    {value: 'rejected', label: 'ردشده'},
];

const orderingOptions = [
    {value: '-created_at', label: 'جدیدترین'},
    {value: 'created_at', label: 'قدیمی‌ترین'},
    {value: '-updated_at', label: 'آخرین ویرایش'},
    {value: 'prompt', label: 'متن سؤال (الفبایی)'},
];

const emptyQuestion = {
    resource_id: '',
    graph_id: '',
    node_id: '',
    node_name: '',
    prompt: '',
    answer: '',
    distractors: ['', '', ''],
    explanation: '',
    context: '',
    learning_objective: '',
    skill: '',
    cognitive_level: '',
    rubric: '',
    quality_flags: '',
    distractor_analysis: '',
    difficulty: 'medium',
    review_status: 'approved',
};

const difficultyLabels = {
    easy: 'آسان',
    medium: 'متوسط',
    hard: 'سخت',
};

const cognitiveLevelLabels = {
    recall: 'یادآوری',
    understand: 'فهم',
    apply: 'کاربرد',
    analyze: 'تحلیل',
    evaluate: 'ارزیابی',
    create: 'خلق',
};

const formatDate = (value) => {
    if (!value) return 'بدون تاریخ';
    try {
        return new Intl.DateTimeFormat('fa-IR', {dateStyle: 'medium'}).format(new Date(value));
    } catch {
        return value;
    }
};

const normalizeCollection = (value) => Array.isArray(value) ? value : [];

const emptyStats = {
    all: 0,
    ready: 0,
    needsReview: 0,
    pending: 0,
    failed: 0,
};

const parseJsonField = (value, fallback) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return fallback;
    try {
        return JSON.parse(trimmed);
    } catch {
        return fallback;
    }
};

const stringifyField = (value) => {
    if (value === undefined || value === null || value === '') return '';
    return JSON.stringify(value, null, 2);
};

const CollapsibleText = ({text, emptyText = 'پاسخی ثبت نشده است.', threshold = 180}) => {
    const [expanded, setExpanded] = useState(false);
    const value = text || emptyText;
    const isLong = value.length > threshold;

    return (
        <div className={`qb-collapsible-text ${expanded ? 'expanded' : ''}`}>
            <p className="qb-directional-text" dir={getTextDirection(value)}><MathText text={value}/></p>
            {isLong && (
                <button type="button" onClick={() => setExpanded((current) => !current)}>
                    {expanded ? 'نمایش کمتر' : 'نمایش بیشتر'}
                </button>
            )}
        </div>
    );
};

const QuestionBank = () => {
    const [searchParams] = useSearchParams();
    const {isPlatformAdmin} = useAuthRole();
    const canDownloadResearchReport = isPlatformAdmin;
    const initialResourceFilter = searchParams.get('resource') || '';
    const [questions, setQuestions] = useState([]);
    const [courses, setCourses] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [reportLoading, setReportLoading] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [reviewUpdatingId, setReviewUpdatingId] = useState(null);
    const [message, setMessage] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [reviewFilter, setReviewFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [difficultyFilter, setDifficultyFilter] = useState('all');
    const [courseFilter, setCourseFilter] = useState('');
    const [documentFilter, setDocumentFilter] = useState(initialResourceFilter);
    const [ordering, setOrdering] = useState('-created_at');
    const [showFilters, setShowFilters] = useState(false);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(24);
    const [pagination, setPagination] = useState({
        count: 0,
        next: null,
        previous: null,
        summary: emptyStats,
    });

    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
    const [selectedQuestionDetails, setSelectedQuestionDetails] = useState({});
    const [reportResource, setReportResource] = useState(initialResourceFilter);
    const [reportSelectedOnly, setReportSelectedOnly] = useState(false);
    const [reportError, setReportError] = useState('');
    const [newQuestion, setNewQuestion] = useState(emptyQuestion);
    const [editingQuestionId, setEditingQuestionId] = useState(null);
    const [importFile, setImportFile] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [exportFilters, setExportFilters] = useState({
        course: '',
        resource: '',
        selectedOnly: false,
        includeAnswers: true,
        includeExplanations: false,
        includeAllStatuses: false,
    });
    const loadData = useCallback(async () => {
        setIsLoading(true);
        setMessage(null);
        try {
            const params = {
                include_invalid: true,
                ordering,
                page,
                page_size: pageSize,
            };
            if (courseFilter) params.course = courseFilter;
            if (documentFilter) params.resource = documentFilter;
            if (statusFilter !== 'all') params.status = statusFilter;
            if (reviewFilter !== 'all') params.review_status = reviewFilter;
            if (typeFilter !== 'all') params.type = typeFilter;
            if (difficultyFilter !== 'all') params.difficulty = difficultyFilter;
            if (debouncedSearchTerm) params.search = debouncedSearchTerm;

            const response = await questionsApi.list(params);
            const pageItems = Array.isArray(response)
                ? response
                : normalizeCollection(response?.results);
            const responseSummary = response?.summary || {};
            const fallbackStats = {
                all: pageItems.length,
                ready: pageItems.filter((question) => question.status === 'ready').length,
                needsReview: pageItems.filter((question) => question.review_status === 'needs_review').length,
                pending: pageItems.filter((question) => question.status === 'pending').length,
                failed: pageItems.filter((question) => question.status === 'failed').length,
            };

            setQuestions(pageItems);
            setPagination({
                count: Array.isArray(response) ? pageItems.length : (response?.count || 0),
                next: response?.next || null,
                previous: response?.previous || null,
                summary: {
                    all: responseSummary.all ?? fallbackStats.all,
                    ready: responseSummary.ready ?? fallbackStats.ready,
                    needsReview: responseSummary.needs_review ?? fallbackStats.needsReview,
                    pending: responseSummary.pending ?? fallbackStats.pending,
                    failed: responseSummary.failed ?? fallbackStats.failed,
                },
            });
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setIsLoading(false);
        }
    }, [
        debouncedSearchTerm,
        courseFilter,
        difficultyFilter,
        documentFilter,
        ordering,
        page,
        pageSize,
        reviewFilter,
        statusFilter,
        typeFilter,
    ]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            accountsApi.courses.list(),
            resourcesApi.list(),
        ]).then(([courseItems, documentItems]) => {
            if (cancelled) return;
            setCourses(normalizeCollection(courseItems));
            setDocuments(normalizeCollection(documentItems));
        }).catch((error) => {
            if (!cancelled) setMessage({type: 'error', text: getErrorMessage(error)});
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setPage(1);
            setDebouncedSearchTerm(searchTerm.trim());
        }, 350);
        return () => window.clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        if (canDownloadResearchReport && searchParams.get('report') === '1') {
            setReportResource(searchParams.get('resource') || '');
            setShowReportModal(true);
        }
    }, [canDownloadResearchReport, searchParams]);

    useEffect(() => {
        if (!showDetailModal) return undefined;
        const previousOverflow = document.body.style.overflow;
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') setShowDetailModal(false);
        };
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', closeOnEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', closeOnEscape);
        };
    }, [showDetailModal]);

    const documentMap = useMemo(() => new Map(documents.map((resource) => [String(resource.resource_id), resource])), [documents]);
    const filteredDocuments = useMemo(() => {
        return documents.filter((resource) => !courseFilter || String(resource.course_id) === courseFilter);
    }, [courseFilter, documents]);
    const exportFilteredDocuments = useMemo(() => documents.filter((resource) =>
        !exportFilters.course || String(resource.course_id) === exportFilters.course
    ), [documents, exportFilters.course]);

    const changeCourseFilter = (value) => {
        setPage(1);
        setCourseFilter(value);
        setDocumentFilter('');
    };

    const applyQuickFilter = (filter, value) => {
        setPage(1);
        if (filter === 'document') {
            setCourseFilter(String(documentMap.get(String(value))?.course_id || ''));
            setDocumentFilter((current) => String(current) === String(value) ? '' : String(value));
        } else if (filter === 'difficulty') {
            setDifficultyFilter((current) => current === value ? 'all' : value);
        } else if (filter === 'type') {
            setTypeFilter((current) => current === value ? 'all' : value);
        } else if (filter === 'status') {
            setStatusFilter((current) => current === value ? 'all' : value);
        } else if (filter === 'review') {
            setReviewFilter((current) => current === value ? 'all' : value);
        }
    };

    const clearFilter = (filter) => {
        setPage(1);
        if (filter === 'status') setStatusFilter('all');
        if (filter === 'review') setReviewFilter('all');
        if (filter === 'type') setTypeFilter('all');
        if (filter === 'difficulty') setDifficultyFilter('all');
        if (filter === 'course') setCourseFilter('');
        if (filter === 'document') setDocumentFilter('');
    };

    const resetAdvancedFilters = () => {
        setPage(1);
        setStatusFilter('all');
        setReviewFilter('all');
        setTypeFilter('all');
        setDifficultyFilter('all');
        setCourseFilter('');
        setDocumentFilter('');
    };

    const filteredQuestions = questions;
    const selectedQuestionIdSet = useMemo(() => new Set(selectedQuestionIds), [selectedQuestionIds]);
    const visibleQuestionIds = useMemo(() => filteredQuestions.map((question) => question.id), [filteredQuestions]);
    const allVisibleSelected = Boolean(visibleQuestionIds.length) && visibleQuestionIds.every((id) => selectedQuestionIdSet.has(id));
    const stats = pagination.summary;
    const totalPages = Math.max(1, Math.ceil(pagination.count / pageSize));
    const firstVisibleQuestion = pagination.count ? ((page - 1) * pageSize) + 1 : 0;
    const lastVisibleQuestion = Math.min(page * pageSize, pagination.count);

    const activeFilterCount = [
        statusFilter !== 'all',
        reviewFilter !== 'all',
        typeFilter !== 'all',
        difficultyFilter !== 'all',
        Boolean(courseFilter),
        Boolean(documentFilter),
    ].filter(Boolean).length;

    const activeFilterChips = useMemo(() => {
        const chips = [];
        if (statusFilter !== 'all') chips.push({key: 'status', label: `پردازش: ${statusLabels[statusFilter]}`});
        if (reviewFilter !== 'all') chips.push({key: 'review', label: `بازبینی: ${reviewStatusLabels[reviewFilter]}`});
        if (typeFilter !== 'all') chips.push({key: 'type', label: `نوع: ${typeFilter === 'test' ? 'تستی' : 'تشریحی'}`});
        if (difficultyFilter !== 'all') chips.push({
            key: 'difficulty',
            label: `سطح: ${difficultyLabels[difficultyFilter]}`
        });
        if (courseFilter) {
            const course = courses.find((item) => String(item.id) === courseFilter);
            chips.push({key: 'course', label: `دوره: ${course?.title || courseFilter}`});
        }
        if (documentFilter) {
            const document = documents.find((item) => String(item.resource_id) === documentFilter);
            chips.push({
                key: 'document',
                label: `منبع: ${document?.original_name || document?.title || documentFilter}`
            });
        }
        return chips;
    }, [
        difficultyFilter,
        courseFilter,
        courses,
        documentFilter,
        documents,
        reviewFilter,
        statusFilter,
        typeFilter,
    ]);

    const resetNewQuestion = () => {
        setNewQuestion(emptyQuestion);
        setEditingQuestionId(null);
    };

    const openDetailModal = (question) => {
        setSelectedQuestion(question);
        setShowDetailModal(true);
    };

    const openAddModal = () => {
        resetNewQuestion();
        setShowAddModal(true);
    };

    const toggleQuestionSelection = (questionId) => {
        const question = questions.find((item) => item.id === questionId);
        if (question) setSelectedQuestionDetails((current) => ({...current, [questionId]: question}));
        setSelectedQuestionIds((current) => (
            current.includes(questionId)
                ? current.filter((id) => id !== questionId)
                : [...current, questionId]
        ));
    };

    const selectVisibleQuestions = () => {
        setSelectedQuestionDetails((current) => ({
            ...current,
            ...Object.fromEntries(questions.map((question) => [question.id, question])),
        }));
        setSelectedQuestionIds((current) => {
            const selected = new Set(current);
            visibleQuestionIds.forEach((id) => selected.add(id));
            return [...selected];
        });
    };

    const clearQuestionSelection = () => {
        setSelectedQuestionIds([]);
        setSelectedQuestionDetails({});
    };

    const openReportModal = (resourceId = documentFilter) => {
        setReportResource(resourceId || '');
        setReportSelectedOnly(selectedQuestionIds.length > 0);
        setReportError('');
        setShowReportModal(true);
    };

    const openExportModal = () => {
        const hasSelectedQuestions = selectedQuestionIds.length > 0;
        setExportFilters({
            course: courseFilter,
            resource: documentFilter,
            selectedOnly: hasSelectedQuestions,
            includeAnswers: true,
            includeExplanations: false,
            includeAllStatuses: hasSelectedQuestions || statusFilter === 'all',
        });
        setShowExportModal(true);
    };

    const openEditModal = (question) => {
        setEditingQuestionId(question.id);
        setNewQuestion({
            resource_id: question.resource_id ? String(question.resource_id) : '',
            graph_id: question.graph_id ? String(question.graph_id) : '',
            node_id: question.node_id || '',
            node_name: question.node_name || '',
            prompt: question.prompt || '',
            answer: question.answer || '',
            distractors: question.distractors?.length ? [...question.distractors] : [''],
            explanation: question.explanation || '',
            context: stringifyField(question.context),
            learning_objective: question.learning_objective || '',
            skill: question.skill || '',
            cognitive_level: question.cognitive_level || '',
            rubric: stringifyField(question.rubric),
            quality_flags: stringifyField(question.quality_flags),
            distractor_analysis: stringifyField(question.distractor_analysis),
            difficulty: question.difficulty || 'medium',
            review_status: question.review_status || 'draft',
        });
        setShowDetailModal(false);
        setShowAddModal(true);
    };

    const updateDistractor = (index, value) => {
        setNewQuestion((previous) => {
            const distractors = [...previous.distractors];
            distractors[index] = value;
            return {...previous, distractors};
        });
    };

    const addDistractor = () => {
        setNewQuestion((previous) => ({...previous, distractors: [...previous.distractors, '']}));
    };

    const removeDistractor = (index) => {
        setNewQuestion((previous) => ({
            ...previous,
            distractors: previous.distractors.filter((_, itemIndex) => itemIndex !== index),
        }));
    };

    const saveQuestion = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        setMessage(null);
        try {
            const payload = {
                resource_id: newQuestion.resource_id,
                prompt: newQuestion.prompt.trim(),
                answer: newQuestion.answer.trim(),
                distractors: newQuestion.distractors.map((item) => item.trim()).filter(Boolean),
                explanation: newQuestion.explanation.trim(),
                difficulty: newQuestion.difficulty,
                review_status: newQuestion.review_status,
            };
            if (newQuestion.graph_id) payload.graph_id = Number(newQuestion.graph_id);
            if (newQuestion.node_id.trim()) payload.node_id = newQuestion.node_id.trim();
            if (newQuestion.node_name.trim()) payload.node_name = newQuestion.node_name.trim();
            if (newQuestion.context.trim()) payload.context = parseJsonField(newQuestion.context, []);
            if (newQuestion.learning_objective.trim()) payload.learning_objective = newQuestion.learning_objective.trim();
            if (newQuestion.skill.trim()) payload.skill = newQuestion.skill.trim();
            if (newQuestion.cognitive_level) payload.cognitive_level = newQuestion.cognitive_level;
            if (newQuestion.rubric.trim()) payload.rubric = parseJsonField(newQuestion.rubric, {});
            if (newQuestion.quality_flags.trim()) payload.quality_flags = parseJsonField(newQuestion.quality_flags, []);
            if (newQuestion.distractor_analysis.trim()) payload.distractor_analysis = parseJsonField(newQuestion.distractor_analysis, []);

            const savedQuestion = editingQuestionId
                ? await questionsApi.update(editingQuestionId, payload)
                : await questionsApi.create(payload);
            const successText = editingQuestionId
                ? 'سؤال با موفقیت ویرایش شد.'
                : 'سؤال با موفقیت به بانک اضافه شد.';
            setShowAddModal(false);
            setSelectedQuestion(savedQuestion || null);
            resetNewQuestion();
            await loadData();
            setMessage({type: 'success', text: successText});
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setIsSubmitting(false);
        }
    };

    const deleteQuestion = async (question) => {
        if (!window.confirm('این سؤال حذف شود؟')) return;

        setDeletingId(question.id);
        setMessage(null);
        try {
            await questionsApi.remove(question.id);
            setQuestions((previous) => previous.filter((item) => item.id !== question.id));
            setSelectedQuestionIds((previous) => previous.filter((id) => id !== question.id));
            setShowDetailModal(false);
            setSelectedQuestion(null);
            const remainingCount = Math.max(0, pagination.count - 1);
            const lastPage = Math.max(1, Math.ceil(remainingCount / pageSize));
            if (page > lastPage) setPage(lastPage);
            else await loadData();
            setMessage({type: 'success', text: 'سؤال حذف شد.'});
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setDeletingId(null);
        }
    };

    const updateReviewStatus = async (question, reviewStatus) => {
        setReviewUpdatingId(question.id);
        setMessage(null);
        try {
            const updatedQuestion = await questionsApi.update(question.id, {review_status: reviewStatus});
            setQuestions((previous) => previous.map((item) => (
                item.id === question.id ? {...item, ...(updatedQuestion || {}), review_status: reviewStatus} : item
            )));
            setSelectedQuestion((current) => (
                current?.id === question.id ? {
                    ...current, ...(updatedQuestion || {}),
                    review_status: reviewStatus
                } : current
            ));
            await loadData();
            setMessage({type: 'success', text: `وضعیت بازبینی به «${reviewStatusLabels[reviewStatus]}» تغییر کرد.`});
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setReviewUpdatingId(null);
        }
    };

    const importExcel = async (event) => {
        event.preventDefault();
        if (!importFile) return;

        setIsImporting(true);
        setMessage(null);
        setImportResult(null);
        try {
            const result = await questionsApi.importExcel(importFile);
            const createdCount = result?.created_count || 0;
            const errorCount = result?.error_count || 0;

            setImportResult(result);
            setImportFile(null);
            await loadData();
            setMessage({
                type: errorCount ? 'warning' : 'success',
                text: errorCount
                    ? `${createdCount} سؤال ثبت شد و ${errorCount} ردیف خطا داشت.`
                    : `${createdCount} سؤال با موفقیت از فایل اکسل ثبت شد.`,
            });
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setIsImporting(false);
        }
    };

    const closeImportModal = () => {
        if (isImporting) return;
        setShowImportModal(false);
        setImportFile(null);
        setImportResult(null);
    };

    const exportQuestionsPdf = async (event) => {
        event?.preventDefault();
        setIsExporting(true);
        setMessage(null);
        try {
            const params = {};
            if (exportFilters.selectedOnly && selectedQuestionIds.length) {
                params.question_ids = selectedQuestionIds.join(',');
                params.include_all_statuses = true;
            } else {
                if (exportFilters.course) params.course = exportFilters.course;
                if (exportFilters.resource) params.resource = exportFilters.resource;
                if (exportFilters.includeAllStatuses) params.include_all_statuses = true;
            }
            if (!exportFilters.includeAnswers) params.include_answers = false;
            if (exportFilters.includeExplanations) params.include_explanations = true;

            const {blob, filename} = await questionsApi.exportPdf(params);
            const url = window.URL.createObjectURL(new Blob([blob], {type: 'application/pdf'}));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename || 'questions.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setShowExportModal(false);
            setMessage({type: 'success', text: 'فایل PDF سؤال‌ها آماده دانلود شد.'});
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setIsExporting(false);
        }
    };

    const downloadResearchReport = async (profile) => {
        if (!reportResource || reportLoading) return;
        setReportError('');
        const params = {resource: reportResource};
        if (reportSelectedOnly && selectedQuestionIds.length) {
            const selected = selectedQuestionIds.map((id) => selectedQuestionDetails[id]);
            if (selected.some((question) => !question || String(question.resource_id) !== String(reportResource) || question.status !== 'ready')) {
                setReportError('برای گزارش انتخابی، همه سؤال‌ها باید آماده و متعلق به منبع انتخاب‌شده باشند.');
                return;
            }
            params.question_ids = selectedQuestionIds.join(',');
        }
        setReportLoading(profile);
        try {
            const result = profile === 'package'
                ? await questionsApi.reportPackage(params)
                : await questionsApi.reportPdf({...params, profile});
            const names = {
                master: 'questions_master.pdf',
                phase_a: 'questions_phase_a.pdf',
                phase_b: 'questions_phase_b.pdf',
                package: 'question_report_package.zip',
            };
            const url = window.URL.createObjectURL(result.blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = result.filename || names[profile];
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            setMessage({type: 'success', text: 'گزارش آماده دانلود شد.'});
        } catch (error) {
            setReportError(getErrorMessage(error));
        } finally {
            setReportLoading('');
        }
    };

    const selectedDocument = selectedQuestion ? documentMap.get(String(selectedQuestion.resource_id)) : null;
    const canSubmit = documentMap.get(String(newQuestion.resource_id))?.can_edit === true
        && newQuestion.prompt.trim() && newQuestion.answer.trim();
    const canImport = Boolean(importFile);

    return (
        <div className="qb-page">
            <section className="qb-hero app-page-header">
                <div>
                    <span className="qb-eyebrow"><FaQuestionCircle/> مدیریت آزمون</span>
                    <h1>بانک سؤالات</h1>
                </div>
                <div className="qb-hero-actions app-page-header-actions">
                    <button className="qb-button secondary" type="button" onClick={openExportModal}
                            disabled={isLoading}>
                        <FiDownload/> خروجی PDF
                    </button>
                    {canDownloadResearchReport && <button className="qb-button secondary" type="button" onClick={() => openReportModal()}>
                        <FiFileText/> گزارش پژوهشی
                    </button>}
                    <button className="qb-button secondary" type="button" onClick={() => setShowImportModal(true)}>
                        <FiUploadCloud/> ایمپورت اکسل
                    </button>
                    <button className="qb-button primary" type="button" onClick={openAddModal}>
                        <FiPlus/> افزودن سؤال
                    </button>
                </div>
            </section>

            <section className="qb-stats" aria-label="خلاصه بانک سوالات">
                <div className="qb-stat"><span className="blue"><FiHelpCircle/></span>
                    <div><strong>{stats.all}</strong><small>کل سؤال‌ها</small></div>
                </div>
                <div className="qb-stat"><span className="green"><FiCheck/></span>
                    <div><strong>{stats.ready}</strong><small>آماده</small></div>
                </div>
                <div className="qb-stat"><span className="purple"><FiShield/></span>
                    <div><strong>{stats.needsReview}</strong><small>نیازمند بازبینی</small></div>
                </div>
                <div className="qb-stat"><span className="amber"><FiLoader/></span>
                    <div><strong>{stats.pending}</strong><small>در انتظار</small></div>
                </div>
                <div className="qb-stat"><span className="red"><FiAlertCircle/></span>
                    <div><strong>{stats.failed}</strong><small>ناموفق</small></div>
                </div>
            </section>

            {message && <div className={`qb-message ${message.type}`}>{message.text}</div>}

            <section className="qb-library">
                <div className="qb-toolbar-sticky">
                    <div className="qb-controls">
                        <label className="qb-search">
                            <FiSearch/>
                            <input
                                type="search"
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                placeholder="جستجو در متن سؤال، پاسخ یا نام منبع..."
                            />
                        </label>
                        <div className="qb-status-tabs">
                            {statusFilters.map((filter) => (
                                <button
                                    key={filter.value}
                                    type="button"
                                    className={statusFilter === filter.value ? 'active' : ''}
                                    onClick={() => {
                                        setPage(1);
                                        setStatusFilter(filter.value);
                                    }}
                                >
                                    {filter.label}
                                </button>
                            ))}
                        </div>
                        <label className="qb-ordering">
                            <FiSliders/>
                            <span>مرتب‌سازی</span>
                            <select value={ordering} onChange={(event) => {
                                setPage(1);
                                setOrdering(event.target.value);
                            }}>
                                {orderingOptions.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </label>
                        <button
                            className={`qb-button filter ${showFilters ? 'active' : ''}`}
                            type="button"
                            aria-expanded={showFilters}
                            aria-controls="question-bank-filters"
                            onClick={() => setShowFilters((value) => !value)}
                        >
                            <FiFilter/> فیلترها
                            {activeFilterCount > 0 && <span className="qb-filter-count">{activeFilterCount}</span>}
                        </button>
                        <button className="qb-icon-button qb-refresh-button" type="button" onClick={loadData}
                                disabled={isLoading} aria-label="به‌روزرسانی" title="به‌روزرسانی">
                            <FiRefreshCw className={isLoading ? 'qb-spin' : ''}/>
                        </button>
                    </div>

                    {activeFilterChips.length > 0 && (
                        <div className="qb-active-filters" aria-label="فیلترهای فعال">
                            <span><FiFilter/> فیلترهای فعال:</span>
                            {activeFilterChips.map((chip) => <button type="button" key={chip.key}
                                                                     onClick={() => clearFilter(chip.key)}
                                                                     title="حذف این فیلتر">{chip.label}<FiX/></button>)}
                            <button className="clear-all" type="button" onClick={resetAdvancedFilters}>پاک‌کردن همه
                            </button>
                        </div>
                    )}

                    {showFilters && (
                        <div className="qb-advanced-filters" id="question-bank-filters">
                            <div className="qb-filters-heading">
                                <div>
                                    <strong>فیلتر سؤال‌ها</strong>
                                    <span>نتایج با تغییر هر فیلتر به‌روز می‌شوند.</span>
                                </div>
                            </div>
                            <label className="qb-filter-wide">
                                <span>دوره</span>
                                <select value={courseFilter} onChange={(event) => changeCourseFilter(event.target.value)}>
                                    <option value="">همه دوره‌ها</option>
                                    {courses.map((course) => <option key={course.id} value={course.id}>
                                        {course.title}{course.organization_name ? ` · ${course.organization_name}` : ''}
                                    </option>)}
                                </select>
                            </label>
                            <label className="qb-filter-wide">
                                <span>منبع</span>
                                <select value={documentFilter} onChange={(event) => {
                                    setPage(1);
                                    setDocumentFilter(event.target.value);
                                }}>
                                    <option value="">همه منابع</option>
                                    {filteredDocuments.map((resource) => <option key={resource.resource_id}
                                                                                 value={resource.resource_id}>{resource.original_name || resource.title}</option>)}
                                </select>
                            </label>
                            <label>
                                <span>نوع سؤال</span>
                                <select value={typeFilter} onChange={(event) => {
                                    setPage(1);
                                    setTypeFilter(event.target.value);
                                }}>
                                    <option value="all">همه نوع‌ها</option>
                                    <option value="test">تستی</option>
                                    <option value="descriptive">تشریحی</option>
                                </select>
                            </label>
                            <label>
                                <span>سطح سؤال</span>
                                <select value={difficultyFilter} onChange={(event) => {
                                    setPage(1);
                                    setDifficultyFilter(event.target.value);
                                }}>
                                    <option value="all">همه سطح‌ها</option>
                                    <option value="easy">آسان</option>
                                    <option value="medium">متوسط</option>
                                    <option value="hard">سخت</option>
                                </select>
                            </label>
                            <label>
                                <span>وضعیت بازبینی</span>
                                <select value={reviewFilter} onChange={(event) => {
                                    setPage(1);
                                    setReviewFilter(event.target.value);
                                }}>
                                    {reviewFilters.map((filter) => <option key={filter.value}
                                                                           value={filter.value}>{filter.label}</option>)}
                                </select>
                            </label>
                        </div>
                    )}

                    {!isLoading && selectedQuestionIds.length > 0 && (
                        <div className="qb-selection-bar">
                            <div>
                                <strong>{selectedQuestionIds.length}</strong>
                                <span>سؤال برای خروجی انتخاب شده است</span>
                            </div>
                            <div>
                                <button type="button" onClick={selectVisibleQuestions}
                                        disabled={!visibleQuestionIds.length || allVisibleSelected}>
                                    انتخاب همه موارد نمایش‌داده‌شده
                                </button>
                                <button type="button" onClick={clearQuestionSelection}
                                        disabled={!selectedQuestionIds.length}>
                                    پاک‌کردن انتخاب‌ها
                                </button>
                                <button type="button" className="primary" onClick={openExportModal}
                                        disabled={!selectedQuestionIds.length}>
                                    <FiDownload/> خروجی انتخاب‌شده‌ها
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {isLoading ? (
                    <div className="qb-state">
                        <FiLoader className="qb-state-icon qb-spin"/>
                        <h3>در حال دریافت سؤال‌ها...</h3>
                    </div>
                ) : filteredQuestions.length ? (
                    <div className="qb-question-list" role="list">
                        {filteredQuestions.map((question) => {
                            const document = documentMap.get(String(question.resource_id));
                            const isSelected = selectedQuestionIdSet.has(question.id);
                            const isTestQuestion = Boolean(question.distractors?.length);
                            return (
                                <article className={`qb-question-row ${isSelected ? 'selected' : ''}`} key={question.id}
                                         role="listitem">
                                    <label className="qb-row-select" title="انتخاب برای خروجی PDF">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleQuestionSelection(question.id)}
                                            aria-label="انتخاب سؤال برای خروجی PDF"
                                        />
                                    </label>
                                    <div className="qb-row-content">
                                        <span className="qb-row-heading">
                                            <button
                                                    className={`qb-type-badge qb-quick-filter ${typeFilter === (isTestQuestion ? 'test' : 'descriptive') ? 'is-filtered' : ''}`}
                                                    onClick={() => applyQuickFilter('type', isTestQuestion ? 'test' : 'descriptive')}
                                                    title="فیلتر بر اساس نوع سؤال">
                                                                                            <span
                                                                                                className={`qb-type-badge ${isTestQuestion ? 'test' : 'descriptive'}`}>
                                                {isTestQuestion ? 'تستی' : 'تشریحی'}
                                            </span>
                                            </button>
                                            <button className="qb-row-title" type="button"
                                                    onClick={() => openDetailModal(question)}>
                                                <strong className="qb-directional-text"
                                                        dir={getTextDirection(question.prompt)}
                                                        title={question.prompt}><MathText text={question.prompt || 'سؤال بدون متن'}/></strong>
                                            </button>
                                        </span>
                                        <span className="qb-row-meta">
                                            <button type="button"
                                                    className={`qb-quick-filter ${String(documentFilter) === String(question.resource_id) ? 'is-filtered' : ''}`}
                                                    onClick={() => applyQuickFilter('document', question.resource_id)}
                                                    title="نمایش سؤال‌های همین منبع"><FiFileText/><span>{document?.original_name || document?.title || `منبع ${question.resource_id}`}</span></button>
                                            <button type="button"
                                                    className={`qb-quick-filter ${difficultyFilter === (question.difficulty || 'medium') ? 'is-filtered' : ''}`}
                                                    onClick={() => applyQuickFilter('difficulty', question.difficulty || 'medium')}
                                                    title="فیلتر بر اساس سطح سؤال"><FiFilter/><span>{difficultyLabels[question.difficulty || 'medium'] || question.difficulty}</span></button>
                                            {question.node_name && <span className="qb-row-node"><FiBookOpen/>{question.node_name}</span>}
                                        </span>
                                    </div>
                                    <div className="qb-row-statuses">
                                        <button type="button"
                                                className={`qb-status qb-quick-filter ${question.status || 'pending'} ${statusFilter === (question.status || 'pending') ? 'is-filtered' : ''}`}
                                                title="فیلتر بر اساس وضعیت پردازش"
                                                onClick={() => applyQuickFilter('status', question.status || 'pending')}>
                                            {statusLabels[question.status] || question.status || 'در انتظار'}
                                        </button>
                                        <button type="button"
                                                className={`qb-review-status qb-quick-filter ${question.review_status || 'draft'} ${reviewFilter === (question.review_status || 'draft') ? 'is-filtered' : ''}`}
                                                title="فیلتر بر اساس وضعیت بازبینی"
                                                onClick={() => applyQuickFilter('review', question.review_status || 'draft')}>
                                            {reviewStatusLabels[question.review_status || 'draft']}
                                        </button>
                                    </div>
                                    <small className="qb-row-date">{formatDate(question.created_at)}</small>
                                    <button className="qb-row-open" type="button"
                                            onClick={() => openDetailModal(question)} aria-label="مشاهده جزئیات سؤال">
                                        <FiEye/>
                                        <span>جزئیات</span>
                                    </button>
                                </article>
                            );
                        })}
                    </div>
                ) : (
                    <div className="qb-state">
                        <span className="qb-empty-icon"><FiHelpCircle/></span>
                        <h3>سؤالی با این مشخصات پیدا نشد</h3>
                        <p>فیلترها را تغییر دهید یا یک سؤال جدید به منابع موجود اضافه کنید.</p>
                    </div>
                )}

                {!isLoading && pagination.count > 0 && (
                    <nav className="qb-pagination" aria-label="صفحه‌بندی سؤال‌ها">
                        <div className="qb-pagination-summary">
                            <strong>{firstVisibleQuestion}–{lastVisibleQuestion}</strong>
                            <span>از {pagination.count} سؤال</span>
                        </div>
                        <div className="qb-pagination-actions">
                            <button
                                type="button"
                                disabled={!pagination.previous || isLoading}
                                onClick={() => setPage((current) => Math.max(1, current - 1))}
                            >
                                صفحه قبل
                            </button>
                            <span>صفحه {page} از {totalPages}</span>
                            <button
                                type="button"
                                disabled={!pagination.next || isLoading}
                                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                            >
                                صفحه بعد
                            </button>
                        </div>
                        <label className="qb-page-size">
                            <span>تعداد در صفحه</span>
                            <select value={pageSize} onChange={(event) => {
                                setPage(1);
                                setPageSize(Number(event.target.value));
                            }}>
                                <option value={12}>۱۲</option>
                                <option value={24}>۲۴</option>
                                <option value={48}>۴۸</option>
                            </select>
                        </label>
                    </nav>
                )}
            </section>

            {showDetailModal && selectedQuestion && (
                <div className="qb-modal-overlay qb-drawer-overlay" onClick={() => setShowDetailModal(false)}>
                    <div className="qb-modal qb-detail-drawer" role="dialog" aria-modal="true" aria-label="جزئیات سؤال"
                         onClick={(event) => event.stopPropagation()}>
                        <div className="qb-modal-header">
                            <div>
                                <span><FiEye/></span>
                                <h2>جزئیات سؤال</h2>
                            </div>
                            <button type="button" aria-label="بستن" onClick={() => setShowDetailModal(false)}><FiX/>
                            </button>
                        </div>
                        <div className="qb-modal-body">
                            <div className="qb-detail-block">
                                <small>متن سؤال</small>
                                <p className="qb-directional-text"
                                   dir={getTextDirection(selectedQuestion.prompt)}><MathText text={selectedQuestion.prompt}/></p>
                            </div>
                            <div className="qb-detail-block success">
                                <small>پاسخ صحیح</small>
                                <CollapsibleText text={selectedQuestion.answer} emptyText="ثبت نشده" threshold={320}/>
                            </div>
                            {!!selectedQuestion.distractors?.length && (
                                <div className="qb-detail-block qb-wrong-answers">
                                    <div className="qb-wrong-answers-heading">
                                        <small>گزینه‌های غلط</small>
                                        <span>{selectedQuestion.distractors.length.toLocaleString('fa-IR')} گزینه</span>
                                    </div>
                                    <ol className="qb-wrong-options">
                                        {selectedQuestion.distractors.map((item, index) => (
                                            <li key={`${item}-${index}`}>
                                                <span className="qb-wrong-option-number" aria-hidden="true">{(index + 1).toLocaleString('fa-IR')}</span>
                                                <p className="qb-directional-text" dir={getTextDirection(item)}>
                                                    <MathText text={item}/>
                                                </p>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            )}
                            {selectedQuestion.explanation && (
                                <div className="qb-detail-block">
                                    <small>توضیح پاسخ</small>
                                    <CollapsibleText text={selectedQuestion.explanation} threshold={320}/>
                                </div>
                            )}
                            {(selectedQuestion.learning_objective || selectedQuestion.skill || selectedQuestion.cognitive_level) && (
                                <div className="qb-detail-block">
                                    <small>هدف و مهارت آموزشی</small>
                                    <div className="qb-review-list">
                                        {selectedQuestion.learning_objective &&
                                            <span><strong>هدف:</strong> <i className="qb-directional-text"
                                                                           dir={getTextDirection(selectedQuestion.learning_objective)}>{selectedQuestion.learning_objective}</i></span>}
                                        {selectedQuestion.skill &&
                                            <span><strong>مهارت:</strong> <i className="qb-directional-text"
                                                                             dir={getTextDirection(selectedQuestion.skill)}>{selectedQuestion.skill}</i></span>}
                                        {selectedQuestion.cognitive_level &&
                                            <span><strong>سطح شناختی:</strong> {cognitiveLevelLabels[selectedQuestion.cognitive_level] || selectedQuestion.cognitive_level}</span>}
                                    </div>
                                </div>
                            )}
                            {selectedQuestion.rubric && (
                                <div className="qb-detail-block">
                                    <small>راهنمای تصحیح</small>
                                    {normalizeCollection(selectedQuestion.rubric.full_credit).length > 0 && (
                                        <div className="qb-checklist">
                                            <strong>امتیاز کامل</strong>
                                            {selectedQuestion.rubric.full_credit.map((item, index) => <label
                                                key={`full-${index}`}><input type="checkbox" readOnly/><span
                                                className="qb-directional-text"
                                                dir={getTextDirection(item)}>{item}</span></label>)}
                                        </div>
                                    )}
                                    {normalizeCollection(selectedQuestion.rubric.partial_credit).length > 0 && (
                                        <div className="qb-checklist">
                                            <strong>امتیاز جزئی</strong>
                                            {selectedQuestion.rubric.partial_credit.map((item, index) => <label
                                                key={`partial-${index}`}><input type="checkbox" readOnly/><span
                                                className="qb-directional-text"
                                                dir={getTextDirection(item)}>{item}</span></label>)}
                                        </div>
                                    )}
                                </div>
                            )}
                            {normalizeCollection(selectedQuestion.quality_flags).length > 0 && (
                                <div className="qb-detail-block warning">
                                    <small>هشدارهای کیفیت</small>
                                    <div className="qb-chip-list">
                                        {selectedQuestion.quality_flags.map((flag, index) => <span
                                            dir={getTextDirection(flag)} key={`${flag}-${index}`}>{flag}</span>)}
                                    </div>
                                </div>
                            )}
                            {normalizeCollection(selectedQuestion.distractor_analysis).length > 0 && (
                                <div className="qb-detail-block">
                                    <small>تحلیل گزینه‌های غلط</small>
                                    <div className="qb-analysis-list">
                                        {selectedQuestion.distractor_analysis.map((item, index) => (
                                            <article key={`${item.option || index}-${index}`}>
                                                <strong className="qb-directional-text"
                                                        dir={getTextDirection(item.option)}>{item.option || `گزینه ${index + 1}`}</strong>
                                                {item.why_plausible &&
                                                    <span><b>چرا محتمل است:</b> <i className="qb-directional-text"
                                                                                   dir={getTextDirection(item.why_plausible)}>{item.why_plausible}</i></span>}
                                                {item.why_wrong &&
                                                    <span><b>چرا غلط است:</b> <i className="qb-directional-text"
                                                                                 dir={getTextDirection(item.why_wrong)}>{item.why_wrong}</i></span>}
                                            </article>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {normalizeCollection(selectedQuestion.context).length > 0 && (
                                <div className="qb-detail-block muted">
                                    <small>زمینه منبع برای استاد</small>
                                    <div className="qb-source-list">
                                        {selectedQuestion.context.slice(0, 4).map((item, index) => (
                                            <article key={index}>
                                                <p className="qb-directional-text"
                                                   dir={getTextDirection(item.snippet || item.text)}>{item.snippet || item.text || '-'}</p>
                                                {item.meta?.page_hint && <span>صفحه {item.meta.page_hint}</span>}
                                            </article>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="qb-detail-grid">
                                <span><strong>منبع:</strong> {selectedDocument?.original_name || selectedDocument?.title || selectedQuestion.resource_id}</span>
                                <span><strong>وضعیت:</strong> {statusLabels[selectedQuestion.status] || selectedQuestion.status}</span>
                                <span><strong>بازبینی:</strong> {reviewStatusLabels[selectedQuestion.review_status || 'draft']}</span>
                                <span><strong>سطح:</strong> {difficultyLabels[selectedQuestion.difficulty || 'medium'] || selectedQuestion.difficulty}</span>
                                <span><strong>گره:</strong> {selectedQuestion.node_name || selectedQuestion.node_id || 'ندارد'}</span>
                                <span><strong>تاریخ:</strong> {formatDate(selectedQuestion.created_at)}</span>
                            </div>
                            {selectedQuestion.resource_id && <Link className="qb-button ghost"
                                                                   to={`/knowledge-graph/${encodeURIComponent(selectedQuestion.resource_id)}`}><FiBookOpen/> مشاهده
                                گراف دانش منبع</Link>}
                        </div>
                        <div className="qb-modal-footer">
                            {selectedQuestion.review_status !== 'approved' && (
                                <button className="qb-button primary" type="button"
                                        disabled={reviewUpdatingId === selectedQuestion.id}
                                        onClick={() => updateReviewStatus(selectedQuestion, 'approved')}>
                                    {reviewUpdatingId === selectedQuestion.id ? <><FiLoader className="qb-spin"/> در حال
                                        ثبت...</> : <><FiCheck/> تایید</>}
                                </button>
                            )}
                            {selectedQuestion.review_status !== 'rejected' && (
                                <button className="qb-button ghost" type="button"
                                        disabled={reviewUpdatingId === selectedQuestion.id}
                                        onClick={() => updateReviewStatus(selectedQuestion, 'rejected')}>
                                    <FiX/> رد
                                </button>
                            )}
                            {selectedQuestion.review_status !== 'draft' && (
                                <button className="qb-button ghost" type="button"
                                        disabled={reviewUpdatingId === selectedQuestion.id}
                                        onClick={() => updateReviewStatus(selectedQuestion, 'draft')}>
                                    <FiFileText/> پیش‌نویس
                                </button>
                            )}
                            <button className="qb-button ghost" type="button"
                                    onClick={() => openEditModal(selectedQuestion)}>
                                <FiEdit/> ویرایش
                            </button>
                            <button
                                className="qb-button danger"
                                type="button"
                                disabled={deletingId === selectedQuestion.id}
                                onClick={() => deleteQuestion(selectedQuestion)}
                            >
                                {deletingId === selectedQuestion.id ? <><FiLoader className="qb-spin"/> در حال
                                    حذف...</> : <><FiTrash2/> حذف</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showAddModal && (
                <div className="qb-modal-overlay" onClick={() => !isSubmitting && setShowAddModal(false)}>
                    <form className="qb-modal qb-add-modal" onSubmit={saveQuestion}
                          onClick={(event) => event.stopPropagation()}>
                        <div className="qb-modal-header">
                            <div>
                                <span><FiPlus/></span>
                                <h2>افزودن سؤال دستی</h2>
                            </div>
                            <button type="button" aria-label="بستن" disabled={isSubmitting}
                                    onClick={() => setShowAddModal(false)}><FiX/></button>
                        </div>
                        <div className="qb-modal-body">
                            {editingQuestionId &&
                                <div className="qb-edit-notice">در حال ویرایش سؤال انتخاب‌شده هستید.</div>}
                            <label className="qb-field">
                                <span>منبع آموزشی</span>
                                <select value={newQuestion.resource_id} onChange={(event) => setNewQuestion({
                                    ...newQuestion,
                                    resource_id: event.target.value
                                })} required>
                                    <option value="">انتخاب منبع</option>
                                    {documents.filter((resource) => resource.can_edit === true).map((resource) => <option key={resource.resource_id}
                                                                         value={resource.resource_id}>{resource.original_name || resource.title}</option>)}
                                </select>
                            </label>
                            <div className="qb-form-grid">
                                <label className="qb-field">
                                    <span>Graph ID</span>
                                    <input type="number" min="1" value={newQuestion.graph_id}
                                           onChange={(event) => setNewQuestion({
                                               ...newQuestion,
                                               graph_id: event.target.value
                                           })}/>
                                </label>
                                <label className="qb-field">
                                    <span>شناسه گره</span>
                                    <input value={newQuestion.node_id} onChange={(event) => setNewQuestion({
                                        ...newQuestion,
                                        node_id: event.target.value
                                    })}/>
                                </label>
                                <label className="qb-field">
                                    <span>نام گره</span>
                                    <input value={newQuestion.node_name} onChange={(event) => setNewQuestion({
                                        ...newQuestion,
                                        node_name: event.target.value
                                    })}/>
                                </label>
                                <label className="qb-field">
                                    <span>وضعیت بازبینی</span>
                                    <select value={newQuestion.review_status} onChange={(event) => setNewQuestion({
                                        ...newQuestion,
                                        review_status: event.target.value
                                    })}>
                                        <option value="draft">پیش‌نویس</option>
                                        <option value="needs_review">نیازمند بازبینی</option>
                                        <option value="approved">تاییدشده</option>
                                        <option value="rejected">ردشده</option>
                                    </select>
                                </label>
                            </div>
                            <label className="qb-field">
                                <span>متن سؤال</span>
                                <textarea dir="auto" value={newQuestion.prompt} onChange={(event) => setNewQuestion({
                                    ...newQuestion,
                                    prompt: event.target.value
                                })} required/>
                            </label>
                            <label className="qb-field">
                                <span>پاسخ صحیح</span>
                                <textarea dir="auto" value={newQuestion.answer} onChange={(event) => setNewQuestion({
                                    ...newQuestion,
                                    answer: event.target.value
                                })} required/>
                            </label>
                            <label className="qb-field">
                                <span>سطح سؤال</span>
                                <select value={newQuestion.difficulty} onChange={(event) => setNewQuestion({
                                    ...newQuestion,
                                    difficulty: event.target.value
                                })}>
                                    <option value="easy">آسان</option>
                                    <option value="medium">متوسط</option>
                                    <option value="hard">سخت</option>
                                </select>
                            </label>
                            <div className="qb-form-grid">
                                <label className="qb-field">
                                    <span>هدف یادگیری</span>
                                    <input dir="auto" value={newQuestion.learning_objective}
                                           onChange={(event) => setNewQuestion({
                                               ...newQuestion,
                                               learning_objective: event.target.value
                                           })}/>
                                </label>
                                <label className="qb-field">
                                    <span>مهارت</span>
                                    <input dir="auto" value={newQuestion.skill} onChange={(event) => setNewQuestion({
                                        ...newQuestion,
                                        skill: event.target.value
                                    })}/>
                                </label>
                                <label className="qb-field">
                                    <span>سطح شناختی</span>
                                    <select value={newQuestion.cognitive_level} onChange={(event) => setNewQuestion({
                                        ...newQuestion,
                                        cognitive_level: event.target.value
                                    })}>
                                        <option value="">ثبت نشود</option>
                                        <option value="recall">یادآوری</option>
                                        <option value="understand">فهم</option>
                                        <option value="apply">کاربرد</option>
                                        <option value="analyze">تحلیل</option>
                                        <option value="evaluate">ارزیابی</option>
                                        <option value="create">خلق</option>
                                    </select>
                                </label>
                            </div>
                            <div className="qb-field">
                                <span>گزینه‌های غلط</span>
                                <div className="qb-distractors">
                                    {newQuestion.distractors.map((item, index) => (
                                        <div key={index} className="qb-distractor-row">
                                            <input dir="auto" value={item}
                                                   onChange={(event) => updateDistractor(index, event.target.value)}
                                                   placeholder={`گزینه ${index + 1}`}/>
                                            {newQuestion.distractors.length > 1 && (
                                                <button type="button" aria-label="حذف گزینه"
                                                        onClick={() => removeDistractor(index)}><FiX/></button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <button className="qb-link-button" type="button" onClick={addDistractor}>افزودن گزینه
                                </button>
                            </div>
                            <label className="qb-field">
                                <span>توضیح پاسخ</span>
                                <textarea dir="auto" value={newQuestion.explanation}
                                          onChange={(event) => setNewQuestion({
                                              ...newQuestion,
                                              explanation: event.target.value
                                          })}/>
                            </label>
                            <label className="qb-field">
                                <span>Rubric JSON</span>
                                <textarea value={newQuestion.rubric} onChange={(event) => setNewQuestion({
                                    ...newQuestion,
                                    rubric: event.target.value
                                })} placeholder='{"full_credit":["..."],"partial_credit":["..."]}'/>
                            </label>
                            <label className="qb-field">
                                <span>Context JSON</span>
                                <textarea value={newQuestion.context} onChange={(event) => setNewQuestion({
                                    ...newQuestion,
                                    context: event.target.value
                                })} placeholder='[{"snippet":"...","meta":{"page_hint":3}}]'/>
                            </label>
                            <label className="qb-field">
                                <span>Quality flags JSON</span>
                                <textarea value={newQuestion.quality_flags} onChange={(event) => setNewQuestion({
                                    ...newQuestion,
                                    quality_flags: event.target.value
                                })} placeholder='["needs_source_check"]'/>
                            </label>
                            <label className="qb-field">
                                <span>Distractor analysis JSON</span>
                                <textarea value={newQuestion.distractor_analysis} onChange={(event) => setNewQuestion({
                                    ...newQuestion,
                                    distractor_analysis: event.target.value
                                })} placeholder='[{"option":"A","why_plausible":"...","why_wrong":"..."}]'/>
                            </label>
                        </div>
                        <div className="qb-modal-footer">
                            <button className="qb-button ghost" type="button" disabled={isSubmitting}
                                    onClick={() => setShowAddModal(false)}>انصراف
                            </button>
                            <button className="qb-button primary" disabled={!canSubmit || isSubmitting}>
                                {isSubmitting ? <><FiLoader className="qb-spin"/> در حال ثبت...</> : <><FiCheck/> ثبت
                                    سؤال</>}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {canDownloadResearchReport && showReportModal && (
                <div className="qb-modal-overlay" onClick={() => !reportLoading && setShowReportModal(false)}>
                    <div className="qb-modal qb-export-modal" role="dialog" aria-modal="true" aria-labelledby="qb-report-title"
                         onClick={(event) => event.stopPropagation()}>
                        <div className="qb-modal-header">
                            <div><span><FiFileText/></span><h2 id="qb-report-title">گزارش پژوهشی سؤال‌ها</h2></div>
                            <button type="button" aria-label="بستن" disabled={!!reportLoading}
                                    onClick={() => setShowReportModal(false)}><FiX/></button>
                        </div>
                        <div className="qb-modal-body qb-report-body">
                            <label className="qb-field">
                                <span>منبع گزارش</span>
                                <select value={reportResource} disabled={!!reportLoading}
                                        onChange={(event) => {setReportResource(event.target.value); setReportError('');}}>
                                    <option value="">منبع را انتخاب کنید</option>
                                    {reportResource && !documentMap.has(String(reportResource)) &&
                                        <option value={reportResource}>{reportResource}</option>}
                                    {documents.map((resource) => <option key={resource.resource_id} value={resource.resource_id}>
                                        {resource.original_name || resource.title}
                                    </option>)}
                                </select>
                            </label>
                            {!!selectedQuestionIds.length && <label className="qb-check-option">
                                <input type="checkbox" checked={reportSelectedOnly} disabled={!!reportLoading}
                                       onChange={(event) => {setReportSelectedOnly(event.target.checked); setReportError('');}}/>
                                <span>فقط {selectedQuestionIds.length} سؤال انتخاب‌شده؛ با خاموش کردن، همه سؤال‌های آماده این منبع گزارش می‌شوند.</span>
                            </label>}
                            <div className="qb-export-summary">
                                <strong>کاربرد فایل‌ها</strong>
                                <p>Master شامل پاسخ، شناسه واقعی و اطلاعات داخلی است. مرحله ۱ فقط صورت سؤال و گزینه‌ها را دارد. مرحله ۲ پاسخ، توضیح، هدف یادگیری و شواهد را نشان می‌دهد.</p>
                                <p>برای داوری کور، مرحله ۱ را پیش از مرحله ۲ ارائه کنید. بسته سه‌گانه شامل Master است و برای مدیر سامانه است. فایل آموزشی منبع را جداگانه در اختیار داور بگذارید.</p>
                            </div>
                            {reportError && <div className="qb-report-error" role="alert"><FiAlertCircle/> {reportError}</div>}
                            <div className="qb-report-actions">
                                {[
                                    {profile: 'master', label: 'Master'},
                                    {profile: 'phase_a', label: 'مرحله ۱'},
                                    {profile: 'phase_b', label: 'مرحله ۲'},
                                    {profile: 'package', label: 'دانلود بسته سه‌گانه'},
                                ].map(({profile, label}) => <button className="qb-button secondary" type="button" key={profile}
                                                               disabled={!reportResource || !!reportLoading}
                                                               onClick={() => downloadResearchReport(profile)}>
                                    {reportLoading === profile ? <FiLoader className="qb-spin"/> : <FiDownload/>}
                                    {reportLoading === profile ? 'در حال دریافت...' : label}
                                </button>)}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showExportModal && (
                <div className="qb-modal-overlay" onClick={() => !isExporting && setShowExportModal(false)}>
                    <form className="qb-modal qb-export-modal" onSubmit={exportQuestionsPdf}
                          onClick={(event) => event.stopPropagation()}>
                        <div className="qb-modal-header">
                            <div>
                                <span><FiDownload/></span>
                                <h2>خروجی PDF سوالات</h2>
                            </div>
                            <button type="button" aria-label="بستن" disabled={isExporting}
                                    onClick={() => setShowExportModal(false)}><FiX/></button>
                        </div>
                        <div className="qb-modal-body">
                            <div className="qb-export-summary">
                                {selectedQuestionIds.length
                                    ? `${selectedQuestionIds.length} سوال انتخاب شده است. می‌توانید فقط همین سوال‌ها را خروجی بگیرید یا فیلترهای پایین را اعمال کنید.`
                                    : 'فیلترهای این بخش مستقیماً برای ساخت PDF ارسال می‌شوند. در حالت پیش‌فرض فقط سوالات آماده خروجی گرفته می‌شوند.'}
                            </div>
                            <label className="qb-check-option">
                                <input
                                    type="checkbox"
                                    checked={exportFilters.selectedOnly}
                                    disabled={!selectedQuestionIds.length}
                                    onChange={(event) => setExportFilters((current) => ({
                                        ...current,
                                        selectedOnly: event.target.checked,
                                        includeAllStatuses: event.target.checked ? true : current.includeAllStatuses,
                                    }))}
                                />
                                <span>فقط سوالات انتخاب‌شده خروجی گرفته شوند</span>
                            </label>
                            <div className="qb-export-grid">
                                <label className="qb-field">
                                    <span>دوره</span>
                                    <select value={exportFilters.course} disabled={exportFilters.selectedOnly}
                                            onChange={(event) => setExportFilters((current) => ({
                                                ...current, course: event.target.value, resource: '',
                                            }))}>
                                        <option value="">همه دوره‌ها</option>
                                        {courses.map((course) => <option key={course.id} value={course.id}>
                                            {course.title}{course.organization_name ? ` · ${course.organization_name}` : ''}
                                        </option>)}
                                    </select>
                                </label>
                                <label className="qb-field">
                                    <span>منبع</span>
                                    <select
                                        value={exportFilters.resource}
                                        disabled={exportFilters.selectedOnly}
                                        onChange={(event) => setExportFilters((current) => ({
                                            ...current,
                                            resource: event.target.value
                                        }))}
                                    >
                                        <option value="">همه منابع</option>
                                        {exportFilteredDocuments.map((resource) => <option key={resource.resource_id}
                                                                                           value={resource.resource_id}>{resource.original_name || resource.title}</option>)}
                                    </select>
                                </label>
                            </div>
                            <div className="qb-export-options">
                                <label className="qb-check-option">
                                    <input
                                        type="checkbox"
                                        checked={exportFilters.includeAnswers}
                                        onChange={(event) => setExportFilters((current) => ({
                                            ...current,
                                            includeAnswers: event.target.checked
                                        }))}
                                    />
                                    <span>پاسخ‌ها داخل PDF باشد</span>
                                </label>
                                <label className="qb-check-option">
                                    <input
                                        type="checkbox"
                                        checked={exportFilters.includeExplanations}
                                        onChange={(event) => setExportFilters((current) => ({
                                            ...current,
                                            includeExplanations: event.target.checked
                                        }))}
                                    />
                                    <span>توضیحات پاسخ داخل PDF باشد</span>
                                </label>
                                <label className="qb-check-option">
                                    <input
                                        type="checkbox"
                                        checked={exportFilters.includeAllStatuses}
                                        disabled={exportFilters.selectedOnly}
                                        onChange={(event) => setExportFilters((current) => ({
                                            ...current,
                                            includeAllStatuses: event.target.checked
                                        }))}
                                    />
                                    <span>همه وضعیت‌ها خروجی گرفته شوند</span>
                                </label>
                            </div>
                        </div>
                        <div className="qb-modal-footer">
                            <button className="qb-button ghost" type="button" disabled={isExporting}
                                    onClick={() => setShowExportModal(false)}>انصراف
                            </button>
                            <button className="qb-button primary" disabled={isExporting}>
                                {isExporting ? <><FiLoader className="qb-spin"/> در حال خروجی...</> : <>
                                    <FiDownload/> دانلود PDF</>}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {showImportModal && (
                <div className="qb-modal-overlay" onClick={closeImportModal}>
                    <form className="qb-modal qb-import-modal" onSubmit={importExcel}
                          onClick={(event) => event.stopPropagation()}>
                        <div className="qb-modal-header">
                            <div>
                                <span><FiUploadCloud/></span>
                                <h2>ایمپورت سؤال از اکسل</h2>
                            </div>
                            <button type="button" aria-label="بستن" disabled={isImporting} onClick={closeImportModal}>
                                <FiX/></button>
                        </div>
                        <div className="qb-modal-body">
                            <label className="qb-dropzone">
                                <FiUploadCloud/>
                                <strong>{importFile ? importFile.name : 'فایل اکسل را انتخاب کنید'}</strong>
                                <small>فقط فایل .xlsx پشتیبانی می‌شود.</small>
                                <input
                                    type="file"
                                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    onChange={(event) => setImportFile(event.target.files?.[0] || null)}
                                />
                            </label>

                            <div className="qb-import-guide">
                                <strong>ستون‌های فایل</strong>
                                <div className="qb-import-columns">
                                    <code>resource_id</code>
                                    <code>prompt</code>
                                    <code>answer</code>
                                    <code>distractors</code>
                                    <code>explanation</code>
                                </div>
                                <p>سه ستون اول اجباری هستند. گزینه‌های غلط را می‌توانید با ; یا | جدا کنید.</p>
                            </div>

                            {importResult && (
                                <div className="qb-import-result">
                                    <div><strong>{importResult.created_count || 0}</strong><span>ثبت‌شده</span></div>
                                    <div><strong>{importResult.error_count || 0}</strong><span>خطادار</span></div>
                                </div>
                            )}

                            {!!importResult?.errors?.length && (
                                <div className="qb-import-errors">
                                    <strong>خطاهای فایل</strong>
                                    <ul>
                                        {importResult.errors.slice(0, 6).map((item, index) => (
                                            <li key={`${item.row || index}-${index}`}>
                                                ردیف {item.row || '-'}: {getErrorMessage({data: item.errors})}
                                            </li>
                                        ))}
                                    </ul>
                                    {importResult.errors.length > 6 &&
                                        <small>{importResult.errors.length - 6} خطای دیگر هم وجود دارد.</small>}
                                </div>
                            )}
                        </div>
                        <div className="qb-modal-footer">
                            <button className="qb-button ghost" type="button" disabled={isImporting}
                                    onClick={closeImportModal}>انصراف
                            </button>
                            <button className="qb-button primary" disabled={!canImport || isImporting}>
                                {isImporting ? <><FiLoader className="qb-spin"/> در حال ایمپورت...</> : <>
                                    <FiUploadCloud/> شروع ایمپورت</>}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default QuestionBank;
